import { getAllHeaders, getDefaultConfig } from '.';
import { MVTLayer, TileLayer } from '@deck.gl/geo-layers';
import type { Feature } from 'geojson';
import type { Dispatch, SetStateAction } from 'react';
import { type TreesAggregateData } from './types';
import { type _Tile2DHeader } from '@deck.gl/geo-layers';
import { generateBase64FilterQuery } from '@/features/filterHelpers';
import { addMvtCachingParams } from '@/features/utils';
import type { FilterState } from '@/features/filterSlice';
import type { GroupId } from '@/features/types';

type TreesTile = _Tile2DHeader & {
  dataInWGS84?: Feature[];
};

type TreeCountCell = {
  counts: {
    total_count: number;
    declining_count: number;
    hazard_count: number;
    strike_count: number;
  };
  bounds: number[][];
};

const getLoadTreeTileDataFn = (
  treeType: string,
  setTreesData: Dispatch<SetStateAction<Feature[]>>
) => {
  return (tiles: TreesTile[]) => {
    // remove all hazard trees
    if (setTreesData) {
      setTreesData((prevTrees: Feature[]) => {
        return prevTrees.filter(
          (tree) => tree.properties?.treeType !== treeType
        );
      });
    }

    tiles.forEach((tile: TreesTile) => {
      const dataInWGS84 = tile.dataInWGS84;
      if (dataInWGS84) {
        const treesWithType: Feature[] = dataInWGS84.map((d: Feature) => ({
          ...d,
          properties: { ...d.properties, treeType },
        }));

        setTreesData((prev: Feature[]) => {
          return [...prev, ...treesWithType];
        });
      }
    });
  };
};

export const getTreeTypeDataTileLayer = (
  treeType: string,
  user: string,
  query: FilterState,
  listId: string | null,
  setTreesData: Dispatch<SetStateAction<Feature[]>>,
  selectedNetworkGroupId: GroupId | null = null,
  activeGroupName: string | null = null
) => {
  let url = `/v1/mvt/trees/{z}/{x}/{y}?tree_type=${treeType}`;
  const params: Record<string, string | number | boolean | null> = {
    filter_query: generateBase64FilterQuery(query),
    list_ids: listId,
  };

  // parameters related to active group
  if (activeGroupName && selectedNetworkGroupId) {
    params.active_group_id = selectedNetworkGroupId;
    params.active_group_name = activeGroupName;
  }

  url = addMvtCachingParams(params, url);

  // Adding this queryHash to have an easy way to recreate the layer, since the tiles in onViewportLoad were giving back cached data - CA-1018
  const queryHash = generateBase64FilterQuery(query);

  return new MVTLayer({
    data: url,
    id: `${treeType}Trees-${queryHash}`,
    loadOptions: {
      fetch: {
        headers: getAllHeaders({}, user),
        credentials: 'include',
      },
    },
    onViewportLoad: getLoadTreeTileDataFn(treeType, setTreesData),
    pickable: false,
    minZoom: 11,
    maxZoom: 24,
    getFillColor: [0, 0, 0, 0],
    getLineColor: [0, 0, 0, 0],
    maxRequests: 6,
    debounceTime: 0.5,
  });
};

export const getTreesAggregateDataTileLayer = (
  setAggregateData: Dispatch<SetStateAction<TreesAggregateData[]>>,
  user: string,
  query: FilterState,
  listId: string | null,
  selectedNetworkGroupId: GroupId | null = null,
  activeGroupName: string | null = null
) => {
  let url = `/v1/mvt/tree_count/{z}/{x}/{y}`;
  const params: Record<string, string | number | boolean | null> = {
    filter_query: generateBase64FilterQuery(query),
    list_ids: listId,
  };
  // parameters related to active group
  if (activeGroupName && selectedNetworkGroupId) {
    params.active_group_id = selectedNetworkGroupId;
    params.active_group_name = activeGroupName;
  }
  url = addMvtCachingParams(params, url);

  return new TileLayer({
    ...getDefaultConfig('tree_count', user),
    id: 'TreeAggregateTileLayer',
    data: url,
    tileSize: 2048,
    onViewportLoad: (tiles: _Tile2DHeader<TreeCountCell[]>[]) => {
      setAggregateData([]);
      for (const tile of tiles) {
        const newCells = (tile.content ?? []).map((c) => ({
          counts: {
            totalCount: c.counts.total_count,
            decliningCount: c.counts.declining_count,
            hazardCount: c.counts.hazard_count,
            strikeCount: c.counts.strike_count,
          },
          bounds: c.bounds,
        }));
        setAggregateData((prev) => [...prev, ...newCells]);
      }
    },
  });
};
